import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-scroll";
import { useScrollProgress, useActiveSection } from "../hooks/useScrollProgress";
import { useTheme } from "../hooks/useTheme";

const NAV_SECTIONS = ["about", "skills", "projects", "experience", "leadership", "contact"];

export default function Navbar() {
  const progress = useScrollProgress();
  const active = useActiveSection(NAV_SECTIONS);
  const { isDark, toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* Scroll Progress */}
      <div
        id="scroll-progress"
        style={{ width: `${progress}%` }}
      />

      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="fixed top-0 left-0 right-0 z-50 px-6 py-4 flex items-center justify-between glassmorphism"
      >
        {/* Logo */}
        <Link to="hero" smooth duration={600} className="cursor-pointer">
          <span className="text-xl font-bold text-gradient font-mono">SM.</span>
        </Link>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-6">
          {NAV_SECTIONS.map((sec) => (
            <li key={sec}>
              <Link
                to={sec}
                smooth
                duration={600}
                offset={-70}
                className={`text-sm font-medium capitalize cursor-pointer transition-colors duration-200 ${
                  active === sec
                    ? "text-accent-purple"
                    : isDark
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {sec}
              </Link>
            </li>
          ))}
        </ul>

        {/* Right controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className="px-3 py-1.5 rounded-full text-xs font-medium border border-white/10 text-gray-400 hover:border-accent-purple/50 hover:text-accent-purple transition-all duration-200"
          >
            {isDark ? "☀ Light" : "🌙 Dark"}
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden flex flex-col gap-1.5 p-2"
          >
            <span className={`block w-5 h-0.5 bg-gray-400 transition-all ${menuOpen ? "rotate-45 translate-y-2" : ""}`} />
            <span className={`block w-5 h-0.5 bg-gray-400 transition-all ${menuOpen ? "opacity-0" : ""}`} />
            <span className={`block w-5 h-0.5 bg-gray-400 transition-all ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`} />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-[65px] left-0 right-0 z-40 glassmorphism px-6 py-4 md:hidden"
          >
            {NAV_SECTIONS.map((sec, i) => (
              <motion.div
                key={sec}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.07 }}
              >
                <Link
                  to={sec}
                  smooth
                  duration={600}
                  offset={-70}
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 text-sm font-medium capitalize text-gray-300 hover:text-white border-b border-white/5 cursor-pointer"
                >
                  {sec}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
